import { Injectable, Injector, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, EMPTY, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SESSION_UID_KEY, SESSION_TOKEN_KEY } from '@shared/constants/api-headers.constants';
import { MsalService } from '@azure/msal-angular';
import { environment } from '@environments/environment';

@Injectable()
export class ApiHeadersInterceptor implements HttpInterceptor {

  /**
   * Lazy-injected to avoid a circular DI chain at module startup:
   * HttpClient → interceptors → MsalService → HttpClient
   */
  private readonly _injector = inject(Injector);
  private _msalService?: MsalService;

  private get msalService(): MsalService {
    return (this._msalService ??= this._injector.get(MsalService));
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // CRITICAL: Skip config.json to avoid circular dependency during app initialization
    if (this.isConfigFile(request.url)) {
      return next.handle(request);
    }

    // No agregar headers corporativos a archivos estáticos HTML para evitar preflight CORS
    // IMPORTANTE: Verificar esto PRIMERO antes de isApiRequest para evitar CORS
    if (this.isStaticHtmlResource(request.url)) {
      return next.handle(request).pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
    }

    // Safety-net: if the session token is missing while MSAL still has an active
    // account, the session is inconsistent (e.g. sessionStorage cleared manually,
    // or logout did not finish before the B2C redirect). Force a full B2C logout
    // so the user gets a clean re-authentication instead of a silent 401.
    // Security endpoints (/security/*) are exempt to avoid circular logout calls.
    if (
      !sessionStorage.getItem(SESSION_TOKEN_KEY) &&
      this.msalService.instance.getActiveAccount() &&
      !this.isSecurityApiRequest(request.url)
    ) {
      this.msalService.logoutRedirect({
        idTokenHint: this.msalService.instance.getActiveAccount()?.idToken,
        postLogoutRedirectUri: environment.B2C_CONFIG.postLogoutRedirectUri
      });
      return EMPTY;
    }

    const modifiedRequest = request.clone({
      setHeaders: this.buildHeaders()
    });

    return next.handle(modifiedRequest);
  }

  /**
   * Detect requests to the security API (session + logout).
   * Exempt from the session-token check to prevent infinite logout loops.
   */
  private isSecurityApiRequest(url: string): boolean {
    try {
      return new URL(url).pathname.includes('/security/');
    } catch {
      return url.includes('/security/');
    }
  }

  /**
   * Verificar si es el archivo de configuración
   * Este archivo debe cargarse ANTES de cualquier interceptor que dependa de la configuración
   */
  private isConfigFile(url: string): boolean {
    return url.includes('assets/config.json') || url.includes('/config.json');
  }

  /**
   * Verificar si es un recurso HTML estático (documentos legales, etc.)
   * Estos no deben tener headers corporativos para evitar preflight CORS
   */
  private isStaticHtmlResource(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname.toLowerCase();
      
      // Excluir archivos HTML estáticos de documentos legales
      return pathname.endsWith('.html') || 
             pathname.includes('/static/') ||
             pathname.includes('/documents/') ||
             pathname.includes('/terminos/') ||
             pathname.includes('/politicas/');
    } catch {
      return false;
    }
  }

  /**
   * Construir headers corporativos según especificación de API
   * @note Authorization header es manejado por MsalInterceptor
   */
  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Request-ID': crypto.randomUUID(),
      'request-date': this.getCurrentISO8601Date(),
      'app-code': 'XP',
      'caller-name': 'xpay-webapp-client',
      'session-UID': this.getOrCreateSessionUid(),
      'orig-device-reference': this.getDeviceReference(),
      'idCode': 'PE'
    };

    const sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (sessionToken) {
      headers['X-Session'] = sessionToken;
    }

    return headers;
  }


  /**
   * Gets or creates a session UID persisted in sessionStorage for the browser session.
   * Created on first API request and reused for subsequent requests.
   */
  private getOrCreateSessionUid(): string {
    let sessionUid = sessionStorage.getItem(SESSION_UID_KEY);
    if (!sessionUid) {
      sessionUid = crypto.randomUUID();
      sessionStorage.setItem(SESSION_UID_KEY, sessionUid);
    }
    return sessionUid;
  }

  /**
   * Obtiene referencia del dispositivo sanitizada
   * Solo permite: letras, números, espacios y guiones
   */
  private getDeviceReference(): string {
    const userAgent = navigator.userAgent.substring(0, 100);
    // Sanitizar: solo permitir [a-zA-Z0-9- ]
    return userAgent.replace(/[^a-zA-Z0-9\- ]/g, '').substring(0, 100);
  }

  /**
   * Genera fecha en formato ISO 8601 con offset de zona horaria
   * Formato esperado: 2020-10-09T14:02:03.987-0500
   */
  private getCurrentISO8601Date(): string {
    const now = new Date();
    const offset = -now.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
    const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
    
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    const second = String(now.getSeconds()).padStart(2, '0');
    const ms = String(now.getMilliseconds()).padStart(3, '0');
    
    return `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}${sign}${hours}${minutes}`;
  }

}